/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import {
  AiEventStreamConnectOptions,
  AiEventStreamEvent,
  AiEventStreamSnapshot,
  AiEventStreamStatus,
  AiEventStreamStoreOptions,
  AiGraphNodeChangeEvent,
  AiPromptGenerationCompleteEvent,
  AiPromptGenerationDeltaEvent,
  AiStreamClosedEvent,
  AiStreamCompleteEvent,
  AiStreamErrorEvent,
  AiStreamStartedEvent,
} from "../types/AiEventStream";

const defaultEventName = "aiEvent";
const defaultMaxStoredEvents = 250;
const defaultMaxStoredDeltas = 1000;

const createInitialSnapshot = (): AiEventStreamSnapshot => ({
  streamId: null,
  status: "idle",
  error: null,
  info: null,
  lastEvent: null,
  events: [],
  promptGeneration: {
    text: "",
    deltas: [],
    lastDelta: null,
    eventCount: 0,
    isComplete: false,
    model: null,
    contentType: "markdown",
    completionSource: null,
  },
  graphNode: {
    nodeName: null,
    graphName: null,
    previousNodeName: null,
    metadata: null,
  },
});

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

const getStringProperty = (value: Record<string, unknown>, key: string): string | null => {
  const propertyValue = value[key];

  return typeof propertyValue === "string" ? propertyValue : null;
};

const getStatusMessage = (event: AiEventStreamEvent, fallback: string): string => {
  if (!isRecord(event)) {
    return fallback;
  }

  const message = getStringProperty(event, "message");
  if (message && message.trim().length > 0) {
    return message;
  }

  const status = getStringProperty(event, "status");
  if (status && status.trim().length > 0) {
    return status;
  }

  return fallback;
};

export class AiEventStreamStore {
  private readonly streamUrlFactory: (streamId: string) => string;
  private readonly eventName: string;
  private readonly withCredentials: boolean;
  private readonly maxStoredEvents: number;
  private readonly maxStoredDeltas: number;

  private eventSource: EventSource | null = null;
  private snapshot: AiEventStreamSnapshot = createInitialSnapshot();
  private closedIntentionally = false;

  private readonly listeners = new Set<() => void>();
  private readonly eventListeners = new Map<string, Set<(event: AiEventStreamEvent) => void | Promise<void>>>();
  private readonly pendingEventListenerPromises = new Set<Promise<void>>();
  private hasDeferredConnectionError = false;
  private hasDeferredClose = false;

  constructor(options: AiEventStreamStoreOptions) {
    this.streamUrlFactory = options.streamUrlFactory;
    this.eventName = options.eventName ?? defaultEventName;
    this.withCredentials = options.withCredentials ?? false;
    this.maxStoredEvents = options.maxStoredEvents ?? defaultMaxStoredEvents;
    this.maxStoredDeltas = options.maxStoredDeltas ?? defaultMaxStoredDeltas;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): AiEventStreamSnapshot => {
    return this.snapshot;
  };

  getCurrentStreamId = (): string | null => {
    return this.snapshot.streamId;
  };

  addEventListener = (
    eventType: string,
    listener: (event: AiEventStreamEvent) => void | Promise<void>
  ): (() => void) => {
    const listeners =
      this.eventListeners.get(eventType) ?? new Set<(event: AiEventStreamEvent) => void | Promise<void>>();
    listeners.add(listener);
    this.eventListeners.set(eventType, listeners);

    return () => {
      listeners.delete(listener);

      if (listeners.size === 0) {
        this.eventListeners.delete(eventType);
      }
    };
  };

  connect = ({ streamId, streamUrl }: AiEventStreamConnectOptions = {}): string => {
    const nextStreamId = streamId ?? crypto.randomUUID();

    this.close();

    this.closedIntentionally = false;
    this.pendingEventListenerPromises.clear();
    this.hasDeferredClose = false;
    this.hasDeferredConnectionError = false;
    this.setSnapshot({
      ...createInitialSnapshot(),
      streamId: nextStreamId,
      status: "connecting",
    });

    const eventSource = new EventSource(streamUrl ?? this.streamUrlFactory(nextStreamId), {
      withCredentials: this.withCredentials,
    });

    this.eventSource = eventSource;

    eventSource.addEventListener(this.eventName, (event) => {
      if (this.eventSource === eventSource) {
        this.handleMessageEvent(event as MessageEvent);
      }
    });

    eventSource.onerror = () => this.handleConnectionError(eventSource);

    return nextStreamId;
  };

  close = (): void => {
    this.closedIntentionally = true;

    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  };

  reset = (): void => {
    this.close();
    this.setSnapshot(createInitialSnapshot());
  };

  private handleMessageEvent(event: MessageEvent): void {
    const parsedEvent = this.parseEvent(event.data);

    if (!parsedEvent || parsedEvent.streamId !== this.snapshot.streamId) {
      return;
    }

    this.notifyTypedEventListeners(parsedEvent);
    this.applyEvent(parsedEvent);
  }

  private parseEvent(rawData: unknown): AiEventStreamEvent | null {
    if (typeof rawData !== "string") {
      return null;
    }

    try {
      const parsed = JSON.parse(rawData);

      if (!isRecord(parsed) || typeof parsed.eventType !== "string") {
        return null;
      }

      return parsed as AiEventStreamEvent;
    } catch {
      return null;
    }
  }

  private applyEvent(event: AiEventStreamEvent): void {
    switch (event.eventType) {
      case "streamStarted":
        this.applyStreamStarted(event as AiStreamStartedEvent);
        return;

      case "promptGenerationDelta":
        this.applyPromptGenerationDelta(event as AiPromptGenerationDeltaEvent);
        return;

      case "promptGenerationComplete":
        this.applyPromptGenerationComplete(event as AiPromptGenerationCompleteEvent);
        return;

      case "graphNodeChange":
        this.applyGraphNodeChange(event as AiGraphNodeChangeEvent);
        return;

      case "streamComplete":
        this.applyStreamComplete(event as AiStreamCompleteEvent);
        return;

      case "streamClosed":
      case "streamCancelled":
        this.applyStreamClosed(event as AiStreamClosedEvent);
        return;

      case "streamError":
        this.applyStreamError(event as AiStreamErrorEvent);
        return;

      default:
        this.setSnapshot({
          ...this.snapshot,
          lastEvent: event,
          events: this.appendStoredEvent(event),
        });
        return;
    }
  }

  private applyStreamStarted(event: AiStreamStartedEvent): void {
    this.setSnapshot({
      ...this.snapshot,
      streamId: event.streamId,
      status: "running",
      error: null,
      info: getStatusMessage(event, "Stream wurde gestartet."),
      lastEvent: event,
      events: this.appendStoredEvent(event),
    });
  }

  private applyPromptGenerationDelta(event: AiPromptGenerationDeltaEvent): void {
    const previousText = this.snapshot.promptGeneration.text;
    const delta = typeof event.delta === "string" ? event.delta : "";
    const nextText = typeof event.generatedText === "string" ? event.generatedText : previousText + delta;
    const nextDeltas = delta.length > 0 ? this.appendStoredDelta(delta) : this.snapshot.promptGeneration.deltas;

    this.setSnapshot({
      ...this.snapshot,
      streamId: event.streamId,
      status: "running",
      error: null,
      info: "Antwort wird generiert.",
      lastEvent: event,
      events: this.appendStoredEvent(event),
      promptGeneration: {
        ...this.snapshot.promptGeneration,
        text: nextText,
        deltas: nextDeltas,
        lastDelta: delta.length > 0 ? delta : this.snapshot.promptGeneration.lastDelta,
        eventCount: this.snapshot.promptGeneration.eventCount + 1,
        model: event.model ?? this.snapshot.promptGeneration.model,
        contentType: "markdown",
        completionSource: null,
      },
    });
  }

  private applyPromptGenerationComplete(event: AiPromptGenerationCompleteEvent): void {
    this.setSnapshot({
      ...this.snapshot,
      streamId: event.streamId,
      status: "completed",
      error: null,
      info: getStatusMessage(event, "Generierung abgeschlossen."),
      lastEvent: event,
      events: this.appendStoredEvent(event),
      promptGeneration: {
        ...this.snapshot.promptGeneration,
        text: event.generatedText,
        isComplete: true,
        model: event.model ?? this.snapshot.promptGeneration.model,
        contentType: "markdown",
        completionSource: "stream",
      },
    });
  }

  private applyGraphNodeChange(event: AiGraphNodeChangeEvent): void {
    const graphPrefix = event.graphName ? `${event.graphName}: ` : "";
    const previousNodeSuffix = event.previousNodeName ? `, vorher: ${event.previousNodeName}` : "";

    this.setSnapshot({
      ...this.snapshot,
      streamId: event.streamId,
      status: "running",
      error: null,
      info: `${graphPrefix}${event.nodeName}${previousNodeSuffix}`,
      lastEvent: event,
      events: this.appendStoredEvent(event),
      graphNode: {
        nodeName: event.nodeName,
        graphName: event.graphName ?? null,
        previousNodeName: event.previousNodeName ?? null,
        metadata: event.metadata ?? null,
      },
    });
  }

  private applyStreamComplete(event: AiStreamCompleteEvent): void {
    this.setSnapshot({
      ...this.snapshot,
      streamId: event.streamId,
      status: "completed",
      error: null,
      info: getStatusMessage(event, "Stream abgeschlossen."),
      lastEvent: event,
      events: this.appendStoredEvent(event),
      promptGeneration: {
        ...this.snapshot.promptGeneration,
        text: event.generatedText ?? this.snapshot.promptGeneration.text,
        isComplete: true,
        contentType: "markdown",
        completionSource: "stream",
      },
    });
  }

  private applyStreamClosed(event: AiStreamClosedEvent): void {
    this.setSnapshot({
      ...this.snapshot,
      streamId: event.streamId,
      status: "completed",
      error: null,
      info: getStatusMessage(event, "Stream geschlossen."),
      lastEvent: event,
      events: this.appendStoredEvent(event),
      promptGeneration: {
        ...this.snapshot.promptGeneration,
        isComplete: true,
      },
    });

    this.closeAfterPendingEventListeners();
  }

  private applyStreamError(event: AiStreamErrorEvent): void {
    this.setSnapshot({
      ...this.snapshot,
      streamId: event.streamId,
      status: "error",
      error: event.message ?? "Beim Stream ist ein Fehler aufgetreten.",
      info: null,
      lastEvent: event,
      events: this.appendStoredEvent(event),
    });

    this.close();
  }

  private appendStoredEvent(event: AiEventStreamEvent): AiEventStreamEvent[] {
    const events = [...this.snapshot.events, event];

    return events.length > this.maxStoredEvents ? events.slice(events.length - this.maxStoredEvents) : events;
  }

  private appendStoredDelta(delta: string): string[] {
    const deltas = [...this.snapshot.promptGeneration.deltas, delta];

    return deltas.length > this.maxStoredDeltas ? deltas.slice(deltas.length - this.maxStoredDeltas) : deltas;
  }

  private notifyTypedEventListeners(event: AiEventStreamEvent): void {
    const listeners = this.eventListeners.get(event.eventType);

    if (!listeners) {
      return;
    }

    const listenerPromises = Array.from(listeners, (listener) => {
      try {
        return Promise.resolve(listener(event));
      } catch (error) {
        return Promise.reject(error);
      }
    });
    const pendingPromise = Promise.allSettled(listenerPromises).then(() => undefined);

    this.pendingEventListenerPromises.add(pendingPromise);
    pendingPromise.finally(() => this.pendingEventListenerPromises.delete(pendingPromise));
  }

  private handleConnectionError = (eventSource: EventSource): void => {
    const streamId = this.snapshot.streamId;
    if (this.closedIntentionally || this.eventSource !== eventSource || streamId === null) {
      return;
    }

    if (this.isTerminalStatus(this.snapshot.status)) {
      this.closeAfterPendingEventListeners();
      return;
    }

    if (this.pendingEventListenerPromises.size > 0) {
      if (this.hasDeferredConnectionError) {
        return;
      }

      this.hasDeferredConnectionError = true;
      Promise.allSettled(Array.from(this.pendingEventListenerPromises)).then(() => {
        if (this.eventSource === eventSource) {
          this.hasDeferredConnectionError = false;
          this.handleConnectionError(eventSource);
        }
      });
      return;
    }

    const event: AiStreamErrorEvent = {
      eventType: "streamError",
      streamId,
      message: "Die Streaming-Verbindung wurde unterbrochen.",
    };
    this.notifyTypedEventListeners(event);
    this.applyStreamError(event);
  };

  private closeAfterPendingEventListeners(): void {
    if (this.pendingEventListenerPromises.size === 0) {
      this.close();
      return;
    }

    if (this.hasDeferredClose) {
      return;
    }

    this.hasDeferredClose = true;
    const eventSource = this.eventSource;
    Promise.allSettled(Array.from(this.pendingEventListenerPromises)).then(() => {
      if (this.eventSource === eventSource && !this.closedIntentionally) {
        this.hasDeferredClose = false;
        this.close();
      }
    });
  }

  private setSnapshot(snapshot: AiEventStreamSnapshot): void {
    this.snapshot = snapshot;
    this.listeners.forEach((listener) => listener());
  }

  private isTerminalStatus(status: AiEventStreamStatus): boolean {
    return status === "completed" || status === "error";
  }

  completePromptGeneration = (
    generatedText: string | null | undefined,
    model?: string,
    contentType: "markdown" | "html" = "html"
  ): void => {
    const finalText = generatedText ?? "";
    const streamId = this.snapshot.streamId ?? crypto.randomUUID();

    const event: AiPromptGenerationCompleteEvent = {
      eventType: "promptGenerationComplete",
      streamId,
      status: "completed",
      generatedText: finalText,
      model,
    };

    this.setSnapshot({
      ...this.snapshot,
      streamId,
      status: "completed",
      error: null,
      info: "Generierung abgeschlossen.",
      lastEvent: event,
      events: this.appendStoredEvent(event),
      promptGeneration: {
        ...this.snapshot.promptGeneration,
        text: finalText,
        isComplete: true,
        model: model ?? this.snapshot.promptGeneration.model,
        contentType,
        completionSource: "api",
      },
    });
  };
}
